import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {useIssueState} from '@github-ui/issue-pr-state'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import type {SubIssueSidePanelItem} from '@github-ui/sub-issues/sub-issue-types'
import {Link, Token} from '@primer/react'
import type React from 'react'

import {useCachePreheatIssueViewer} from '../../hooks/use-cache-preheat-issueviewer'
import {getParentIssueUrl} from '../../utils/urls'
import type {OptionConfig} from '../OptionConfig'
import type {
  IssueSecondaryParentNode,
  IssueSecondaryParentRepositoryNode,
  IssueSecondaryParentRepositoryOwnerNode,
} from './header-parent-title-query-types'
import dividerLineStyles from './HeaderMetadataDivider.module.css'
import styles from './HeaderParentTitle.module.css'

// the generated `parent` type is the union of every fragment's parent selection in the secondary
// query, so we Pick out just the fields this component reads. we have to Pick at each level
// (parent, repository, owner) because the nested objects carry extra fields as well
export type HeaderParentFragment = RelayCompat<
  Pick<IssueSecondaryParentNode, 'id' | 'number' | 'state' | 'stateReason' | 'title' | 'titleHTML' | 'url'> & {
    readonly repository: Pick<IssueSecondaryParentRepositoryNode, 'name'> & {
      readonly owner: Pick<IssueSecondaryParentRepositoryOwnerNode, 'login'>
    }
  }
>

type HeaderParentTitleProps = {
  parent?: HeaderParentFragment | null
  small?: boolean
} & SharedProps

type HeaderParentTitleInternalProps = {
  parent: HeaderParentFragment
  small?: boolean
} & SharedProps

type SharedProps = {
  optionConfig?: OptionConfig
  repositoryNameWithOwner: string
}

/** The internal component that renders the parent title itself after all data fetching has occurred */
export const HeaderParentTitle = ({
  parent,
  optionConfig,
  small = false,
  repositoryNameWithOwner,
}: HeaderParentTitleProps) => {
  // The data from the secondary query is not _all_ available yet, however,
  // AddSubIssueButtonGroup requests parent.id which is available in the primary query. The store
  // merges this partial data and we are left with a parent object that is not empty, but also not fully populated.
  // See: https://github.com/github/github/issues/333236 and https://github.com/github/issues/issues/17680
  if (!parent?.repository || !parent.repository.owner) {
    return null
  }

  return (
    <HeaderParentTitleInternal
      parent={parent}
      optionConfig={optionConfig}
      small={small}
      repositoryNameWithOwner={repositoryNameWithOwner}
    />
  )
}

const HeaderParentTitleInternal = ({
  parent,
  optionConfig,
  small = false,
  repositoryNameWithOwner,
}: HeaderParentTitleInternalProps) => {
  const repo = parent.repository.name
  const owner = parent.repository.owner.login

  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const hovercardAttrs = getHovercardAttributesForType('issue', {owner, repo, issueNumber: parent.number})

  useCachePreheatIssueViewer({
    owner,
    repo,
    number: parent.number,
    source: 'HeaderParentTitle',
    tabbedTimeline: optionConfig?.tabbedTimeline,
  })

  const {sourceIcon} = useIssueState({
    state: parent.state,
    stateReason: parent.stateReason,
  })

  const IconComponent = sourceIcon('Issue')

  if (!IconComponent) return null

  const timelineSidePanelEnabled = isFeatureEnabled('issues_react_timeline_side_panel')

  const handleOpen = (e: React.MouseEvent | React.KeyboardEvent) => {
    // If in the side panel and the parent issue is the same as the current issue, just close the side panel
    if (optionConfig?.onParentIssueActivate) {
      const itemIdentifier = {number: parent.number, repo, owner, type: 'Issue'} as const
      const closedSidePanel = optionConfig.onParentIssueActivate(e, itemIdentifier)
      if (closedSidePanel) return
    }

    // If onSubIssueClick is available, open the parent issue in the issue viewer (side panel)
    const canOpenInSidePanel = timelineSidePanelEnabled || optionConfig?.insideSidePanel
    if (canOpenInSidePanel && optionConfig?.onSubIssueClick) {
      const {id, number, state, title, url} = parent
      const subIssueItem: SubIssueSidePanelItem = {
        id: parseInt(id, 10),
        number,
        owner,
        repo,
        state,
        title,
        url,
      }
      e.preventDefault()
      optionConfig.onSubIssueClick(subIssueItem)
      return
    }

    if (optionConfig?.navigate) {
      // Fallback: navigate to the parent issue
      e.preventDefault()
      const parentRepoNameWithOwner = `${owner}/${repo}`
      optionConfig?.navigate?.(
        getParentIssueUrl(repositoryNameWithOwner, parentRepoNameWithOwner, parent.number, parent.url),
      )
    }
  }

  const handleClick = (e: React.MouseEvent) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return
    handleOpen(e)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (e.key !== 'Enter' || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return
    handleOpen(e)
  }

  /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side */
  const title = parent.titleHTML as SafeHTMLString

  const tokenContent = (
    <div className={styles.parentTitleContainer}>
      {/* eslint-disable-next-line react-hooks/static-components */}
      <IconComponent size={14} />
      <span className={styles.parentLabel}>Parent:</span>
      <Link
        aria-label={`Parent issue: ${parent.title}`}
        inline
        muted
        hoverColor="var(--fgColor-muted)"
        href={parent.url}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        {...hovercardAttrs}
        className={styles.parentTitleLink}
      >
        {small ? `#${parent.number}` : <VerifiedHTMLText html={title} className="markdown-title" />}
      </Link>
    </div>
  )

  if (small) return tokenContent

  return (
    <>
      <span className={dividerLineStyles.dividerLine} />
      <div className={styles.parentTitleWrapper}>
        <Token text={tokenContent} size={'xlarge'} className={styles.parentTitleToken} />
      </div>
    </>
  )
}
